import React from 'react';
import { i18n as _i18n } from "@lingui/core";
import { Trans } from '@lingui/react';
import { useAppConfiguration, useTrackEvent } from '@wkda/funnel-components';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function GoogleRatingLogo() {
  return /*#__PURE__*/_jsx("img", {
    src: "https://content.wirkaufendeinauto.de/static/car_images/google-ratings-logo.svg",
    alt: "Google Rating Logo",
    className: "google-ratings__logo",
    loading: "lazy"
  });
}
export function GoogleRating() {
  return /*#__PURE__*/_jsx("img", {
    src: "https://content.wirkaufendeinauto.de/static/car_images/google-ratings.svg",
    alt: "Google Rating Logo",
    className: "google-ratings__logo--small",
    loading: "lazy"
  });
}
export function GoogleRatingStars(_ref) {
  let {
    variant = 'default'
  } = _ref;
  const appConfig = useAppConfiguration();

  // @ts-ignore
  if (appConfig?.marketingConfig?.googleRatingsSingleStar && variant === 'default') {
    return /*#__PURE__*/_jsx("img", {
      src: "https://content.wirkaufendeinauto.de/static/car_images/google-ratings-star.svg",
      alt: "Google Rating Star",
      className: "",
      loading: "lazy"
    });
  }
  return /*#__PURE__*/_jsx("img", {
    src: "https://content.wirkaufendeinauto.de/static/car_images/google-ratings-stars.svg",
    alt: "Google Rating Stars",
    className: "google-ratings__stars",
    loading: "lazy"
  });
}
export function GoogleRatingFigures(_ref2) {
  let {
    withReviews = true,
    onReviewsClick = null,
    className = '',
    variant = 'default'
  } = _ref2;
  const appConfig = useAppConfiguration();

  // @ts-ignore
  const reviewsAs = appConfig?.marketingConfig?.googleRatingsReviewsAs;

  // Determine reviews display type
  // Priority: withReviews=false always wins, then check config for text vs link
  let reviewsType;
  if (withReviews === false) {
    reviewsType = 'none';
  } else {
    // withReviews is true - check config for text vs link, default to text
    reviewsType = reviewsAs === 'link' && variant === 'default' ? 'link' : 'text';
  }
  const renderReviews = () => {
    if (reviewsType === 'none') {
      return null;
    }
    const reviewsText = _i18n._(
    /*i18n*/
    {
      id: 'carlead.ratings.google.reviews'
    });
    if (reviewsType === 'link') {
      return /*#__PURE__*/_jsxs("span", {
        role: "button",
        className: "d-flex gap-1 google-ratings__reviews google-ratings__reviews--link",
        onClick: onReviewsClick,
        "aria-label": reviewsText,
        children: [/*#__PURE__*/_jsx("span", {
          className: "google-ratings__reviews--link-text",
          children: reviewsText
        }), /*#__PURE__*/_jsx("img", {
          src: "https://content.wirkaufendeinauto.de/static/car_images/google-ratings-link-external.svg",
          alt: "Google Rating Arrow",
          className: "mt-n1"
        })]
      });
    }
    return /*#__PURE__*/_jsx("p", {
      className: "google-ratings__reviews google-ratings__reviews--text",
      children: reviewsText
    });
  };
  return /*#__PURE__*/_jsxs("div", {
    className: `google-ratings__figures ${className}`,
    children: [/*#__PURE__*/_jsx("p", {
      className: "google-ratings__score",
      children: _i18n._(
      /*i18n*/
      {
        id: 'carlead.ratings.google.score'
      })
    }), renderReviews()]
  });
}
export function ReviewsIndicators(props) {
  const {
    currentIndex,
    maxScrollIndex,
    scrollToCard,
    handleKeyDown,
    reviews
  } = props;
  const track = useTrackEvent();
  const handleClick = index => {
    scrollToCard(index);
    track({
      action: 'eventTracking',
      category: 'lead',
      eventData: {
        category: 'Google Ratings',
        action: 'Click',
        label: 'Google Ratings Dot'
      }
    });
  };
  return /*#__PURE__*/_jsxs("nav", {
    className: "google-ratings-reviews__indicators",
    "aria-label": _i18n._(
    /*i18n*/
    {
      id: 'carlead.ratings.reviews.google.aria.navigation'
    }),
    children: [/*#__PURE__*/_jsx("span", {
      className: "google-ratings-reviews__indicators-label",
      id: "indicators-label",
      children: /*#__PURE__*/_jsx(Trans, {
        id: "carlead.ratings.reviews.google.indicator_counter",
        values: {
          current: currentIndex + 1,
          total: maxScrollIndex + 1
        }
      })
    }), /*#__PURE__*/_jsx("div", {
      className: "google-ratings-reviews__indicators-dots",
      role: "tablist",
      "aria-labelledby": "indicators-label",
      children: [...Array(Math.max(1, maxScrollIndex + 1))].map((_, index) => {
        // Calculate which card this indicator represents
        const totalCards = reviews.length + 1;
        const safeMaxIndex = Math.max(1, maxScrollIndex);
        const cardIndex = Math.floor(index / safeMaxIndex * (totalCards - 1));
        const isGoogleCard = cardIndex === reviews.length;
        const cardName = isGoogleCard ? 'Google' : reviews[cardIndex]?.name;
        return /*#__PURE__*/_jsx("button", {
          className: `google-ratings-reviews__indicator ${index === currentIndex ? 'google-ratings-reviews__indicator--active' : ''}`,
          onClick: () => handleClick(index),
          onKeyDown: e => handleKeyDown(e, index),
          role: "tab",
          "aria-selected": index === currentIndex,
          "aria-controls": `review-${index}`,
          "aria-label": `${_i18n._(
          /*i18n*/
          {
            id: 'carlead.ratings.reviews.google.aria.view'
          })} ${cardName}`,
          tabIndex: index === currentIndex ? 0 : -1
        }, index);
      })
    })]
  });
}
export const GoogleReviewCard = props => {
  const {
    review,
    index,
    renderStars
  } = props;
  const hasLocalGuideBadge = /local guide/i.test(review.subtitle);
  return /*#__PURE__*/_jsxs("article", {
    className: "google-ratings-reviews__card",
    "aria-labelledby": `reviewer-${review.id}`,
    children: [/*#__PURE__*/_jsxs("header", {
      className: "google-ratings-reviews__card-header",
      children: [/*#__PURE__*/_jsxs("div", {
        className: "google-ratings-reviews__avatar-wrapper",
        children: [/*#__PURE__*/_jsx("img", {
          src: review.avatar,
          alt: `${_i18n._(
          /*i18n*/
          {
            id: 'carlead.ratings.reviews.google.aria.profile'
          })} ${review.name}`,
          className: "google-ratings-reviews__avatar",
          loading: "lazy",
          height: 60,
          width: 60
        }), hasLocalGuideBadge && /*#__PURE__*/_jsx("img", {
          src: "https://content.wirkaufendeinauto.de/static/car_images/google-ratings-reviewer-verification.svg",
          alt: _i18n._(
          /*i18n*/
          {
            id: 'carlead.ratings.reviews.google.aria.local_guide'
          }),
          className: "google-ratings-reviews__local-guide-badge"
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: "google-ratings-reviews__reviewer-info",
        children: [/*#__PURE__*/_jsx("h3", {
          id: `reviewer-${review.id}`,
          className: "google-ratings-reviews__reviewer-name",
          children: review.name
        }), /*#__PURE__*/_jsx("p", {
          className: "google-ratings-reviews__reviewer-subtitle",
          children: review.subtitle
        }), renderStars(review.rating)]
      })]
    }), /*#__PURE__*/_jsx("blockquote", {
      className: "google-ratings-reviews__review-text",
      children: review.text
    }), /*#__PURE__*/_jsxs("footer", {
      className: "google-ratings-reviews__card-footer",
      children: [/*#__PURE__*/_jsx("time", {
        className: "google-ratings-reviews__visit-date",
        dateTime: review.visitDate,
        children: review.visitDate
      }), /*#__PURE__*/_jsx(GoogleRating, {})]
    })]
  }, review.id);
};
export const GoogleReviewsSummaryCard = () => {
  const track = useTrackEvent();
  return /*#__PURE__*/_jsx("article", {
    className: "google-ratings-reviews__card google-ratings-reviews__card--summary",
    children: /*#__PURE__*/_jsxs("div", {
      className: "google-ratings-reviews__summary-content",
      children: [/*#__PURE__*/_jsx(GoogleRatingLogo, {}), /*#__PURE__*/_jsxs("div", {
        className: "google-ratings-reviews__summary-rating",
        children: [/*#__PURE__*/_jsx(GoogleRatingFigures, {
          withReviews: false
        }), /*#__PURE__*/_jsx(GoogleRatingStars, {
          variant: "compact"
        })]
      }), /*#__PURE__*/_jsx("span", {
        className: "google-ratings-reviews__more-link",
        onClick: () => {
          track({
            action: 'eventTracking',
            category: 'lead',
            eventData: {
              category: 'Google Ratings',
              action: 'Click',
              label: 'Google Ratings More Link'
            }
          });
        },
        children: _i18n._(
        /*i18n*/
        {
          id: 'carlead.ratings.reviews.google.more_link'
        })
      })]
    })
  });
};
export const GoogleReviewHeaderNav = _ref3 => {
  let {
    scrollNavigation
  } = _ref3;
  const track = useTrackEvent();
  const handleClick = direction => {
    track({
      action: 'eventTracking',
      category: 'lead',
      eventData: {
        category: 'Google Ratings',
        action: 'Click',
        label: `Google Ratings ${direction} Button`
      }
    });
    direction === 'Left' ? scrollNavigation.scrollToPrevious() : scrollNavigation.scrollToNext();
  };
  return /*#__PURE__*/_jsxs("div", {
    className: "google-ratings-reviews__header-nav",
    children: [/*#__PURE__*/_jsx("button", {
      className: "google-ratings-reviews__header-nav-button google-ratings-reviews__header-nav-button--left",
      onClick: () => handleClick('Left'),
      disabled: !scrollNavigation.canScrollLeft,
      "aria-label": "scroll left"
    }), /*#__PURE__*/_jsx("button", {
      className: "google-ratings-reviews__header-nav-button google-ratings-reviews__header-nav-button--right",
      onClick: () => handleClick('Right'),
      disabled: !scrollNavigation.canScrollRight,
      "aria-label": "scroll right"
    })]
  });
};